import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import type { CalendarEventReminderInfoForType } from "@tellescope/types-models";
import { Checkbox, Combobox, type ComboboxOption, Label, useDebouncedValue } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type JourneyReminder =
  | CalendarEventReminderInfoForType["add-to-journey"]
  | CalendarEventReminderInfoForType["Remove From Journey"];

export interface JourneyInfoSectionProps<T extends JourneyReminder> {
  value: T;
  onChange: (newValue: T) => void;
  mode: "add" | "remove";
}

export const JourneyInfoSection = <T extends JourneyReminder>({
  value,
  onChange,
  mode,
}: JourneyInfoSectionProps<T>) => {
  const { t } = useTranslation("calendar");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);

  const journeyQuery = useTellescopeInfiniteQuery({
    model: "journeys",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedSearchQuery
        ? {
            query: debouncedSearchQuery,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const selectedJourneyQuery = useTellescopeQuery({
    model: "journeys",
    method: "getOne",
    request: value.info.journeyId,
    enabled: !!value.info.journeyId,
  });

  const journeyOptions = useMemo<ComboboxOption[]>(
    () =>
      journeyQuery.data?.map((journey) => ({
        value: journey.id,
        label: journey.title,
      })) ?? [],
    [journeyQuery.data]
  );

  const selectedJourneyOption = useMemo<ComboboxOption | undefined>(() => {
    if (!value.info.journeyId) return undefined;
    const fromList = journeyQuery.data?.find(({ id }) => id === value.info.journeyId);
    if (fromList) return { value: fromList.id, label: fromList.title };
    if (selectedJourneyQuery.data) {
      return { value: selectedJourneyQuery.data.id, label: selectedJourneyQuery.data.title };
    }
    return undefined;
  }, [value.info.journeyId, journeyQuery.data, selectedJourneyQuery.data]);

  return (
    <>
      <Combobox
        className="w-full"
        value={selectedJourneyOption}
        onChange={(option) =>
          onChange({
            ...value,
            info: {
              ...value.info,
              journeyId: option?.value ?? "",
            },
          })
        }
        options={journeyOptions}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customFilter={(_, options) => options}
        placeholder={t("Select journey")}
        searchPlaceholder={t("Search journeys...")}
        isLoading={journeyQuery.isLoading}
        loadingText={t("Loading journeys...")}
        emptyText={t("No journeys found")}
        formatSelectedValue={({ label }) => <p className="text-xs font-light">{label}</p>}
      />
      <div className="space-y-2">
        <span className="flex gap-2 items-center">
          <Checkbox
            id="journey-timing-passed"
            checked={mode === "add" ? !value.dontSendIfPassed : !!value.dontSendIfPassed}
            onCheckedChange={(checked) =>
              onChange({
                ...value,
                dontSendIfPassed: mode === "add" ? !checked : !!checked,
              })
            }
          />
          <Label htmlFor="journey-timing-passed" className="text-muted-foreground text-xs font-light">
            {mode === "add" ? t("Send even if timing has passed") : t("Don't send if timing has passed")}
          </Label>
        </span>
        <span className="flex gap-2 items-center">
          <Checkbox
            id="journey-dont-send-if-joined"
            checked={!!value.dontSendIfJoined}
            onCheckedChange={(checked) =>
              onChange({
                ...value,
                dontSendIfJoined: !!checked,
              })
            }
          />
          <Label htmlFor="journey-dont-send-if-joined" className="text-muted-foreground text-xs font-light">
            {t("Don't send if contact joined video call")}
          </Label>
        </span>
        {mode === "add" && (
          <span className="flex gap-2 items-center">
            <Checkbox
              id="journey-first-attendee-only"
              checked={"firstAttendeeOnly" in value.info ? !!value.info.firstAttendeeOnly : false}
              onCheckedChange={(checked) =>
                onChange({
                  ...value,
                  info: {
                    ...value.info,
                    firstAttendeeOnly: !!checked,
                  },
                })
              }
            />
            <Label htmlFor="journey-first-attendee-only" className="text-muted-foreground text-xs font-light">
              {t("First attendee only")}
            </Label>
          </span>
        )}
      </div>
    </>
  );
};
